<template>
    <div class="prefix-suffix-container">
      <!-- Main input area -->
      <ion-list :inset="!no_list" style="background:unset">
        <!-- Component name field - Full width for better handling of long names -->
        <div class="component-name-wrapper">
          <input 
            class="component-input" 
            v-model="selected_component.name" 
            placeholder="Component naam"
            aria-label="Component naam"
          />
        </div>
        
        <!-- Prefix and Suffix controls -->
        <div class="controls-wrapper">
          <!-- Prefix button -->
          <div class="control-button prefix-button" @click="openPopover('prefix', $event)">
            <span class="button-label">Voorvoegsel</span>
            <span class="button-value" v-if="prefix">{{ prefix }}</span>
            <span class="button-placeholder" v-else>Klik om toe te voegen</span>
          </div>
          
          <!-- Suffix button -->
          <div class="control-button suffix-button" @click="openPopover('suffix', $event)">
            <span class="button-label">Achtervoegsel</span>
            <span class="button-value" v-if="suffix">{{ suffix }}</span>
            <span class="button-placeholder" v-else>Klik om toe te voegen</span>
          </div>
        </div>
  
        <!-- Amount selector -->
        <ion-item class="amount-selector">
          <ion-label>Aantal componenten</ion-label>
          <ion-select 
            mode="ios" 
            okText="Bevestigen" 
            cancelText="Annuleren" 
            type="number" 
            v-model="amount"
            aria-label="Aantal componenten"
          >
            <ion-select-option v-for="number in 50" :value="number" :key="number">
              {{ number }}
            </ion-select-option>
          </ion-select>
        </ion-item>
      </ion-list>
  
      <!-- Preview section (only shown when amount > 1) -->
      <ion-list v-if="amount > 1" :inset="true" class="preview-list">
        <ion-list-header class="preview-header">
          <ion-label>Voorbeeld</ion-label>
        </ion-list-header>
  
        <ion-item v-for="(example, index) in exampleComponents" :key="index" :style="getItemStyle(index)">
          <ion-label>{{ example }}</ion-label>
        </ion-item>
  
        <ion-item v-if="amount > 3" class="more-items-indicator">
          <ion-label class="text-medium">
            <i>Nog {{ amount - 3 }} {{ amount - 3 === 1 ? 'component' : 'componenten' }}...</i>
          </ion-label>
        </ion-item>
      </ion-list>
  
      <!-- Popover for prefix settings -->
      <ion-popover ref="prefixPopover" :event="popoverEvent" :translucent="false" class="settings-popover">
        <ion-content>
          <ion-list class="popover-list">
            <ion-item>
              <ion-label position="stacked">Voorvoegsel</ion-label>
              <ion-input v-model="prefix" placeholder="Voer voorvoegsel in"></ion-input>
            </ion-item>
            <ion-item lines="none">
              <ion-label>Oplopend nummeren</ion-label>
              <ion-toggle slot="end" v-model="prefixCountUp"></ion-toggle>
            </ion-item>
            <ion-item v-if="prefixCountUp" class="help-text" lines="none">
              <ion-note>Nummers in het voorvoegsel worden automatisch verhoogd voor elke component</ion-note>
            </ion-item>
          </ion-list>
        </ion-content>
      </ion-popover>
  
      <!-- Popover for suffix settings -->
      <ion-popover ref="suffixPopover" :event="popoverEvent" :translucent="false" class="settings-popover">
        <ion-content>
          <ion-list class="popover-list">
            <ion-item>
              <ion-label position="stacked">Achtervoegsel</ion-label>
              <ion-input v-model="suffix" placeholder="Voer achtervoegsel in"></ion-input>
            </ion-item>
            <ion-item lines="none">
              <ion-label>Oplopend nummeren</ion-label>
              <ion-toggle slot="end" v-model="suffixCountUp"></ion-toggle>
            </ion-item>
            <ion-item v-if="suffixCountUp" class="help-text" lines="none">
              <ion-note>Nummers in het achtervoegsel worden automatisch verhoogd voor elke component</ion-note>
            </ion-item>
          </ion-list>
        </ion-content>
      </ion-popover>
    </div>
  </template>
  
  <script>
  import { IonPopover, IonContent, IonNote } from '@ionic/vue';
  
  export default {
    components: {
      IonPopover,
      IonContent,
      IonNote
    },
    props: {
      component: {
        type: Object,
        required: true
      },
      no_list: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        prefix: '',
        suffix: '',
        prefixCountUp: false,
        suffixCountUp: false,
        amount: 1,
        selected_component: {
          name: 'Component'
        },
        popoverEvent: null,
        activePopover: null
      }
    },
    created() {
      this.selected_component = { ...this.component };
    },
    methods: {
      /**
       * Increments the last number found in a string
       * @param {string} base - The base string containing a number
       * @param {number} increment - The value to add to the number
       * @returns {string} - The string with the incremented number
       */
      incrementCounter(base, increment) {
        if (!base) return '';
        
        const baseParts = base.match(/(.*?)(\d+)(.*)$/);
        if (baseParts) {
          const prefixPart = baseParts[1];
          const numberPart = parseInt(baseParts[2], 10);
          const suffixPart = baseParts[3];
          const incrementedNumber = numberPart + increment;
          return `${prefixPart}${incrementedNumber}${suffixPart}`;
        } else {
          return `${base}${increment + 1}`;
        }
      },
      
      /**
       * Returns a style object for preview items to create a fading effect
       * @param {number} index - The index of the item
       * @returns {Object} - The style object with opacity
       */
      getItemStyle(index) {
        const opacityValues = [1, 0.7, 0.4];
        return {
          opacity: opacityValues[index] || 1
        };
      },
      
      /**
       * Opens the appropriate popover
       * @param {string} type - Either 'prefix' or 'suffix'
       * @param {Event} event - The click event
       */
      openPopover(type, event) {
        this.popoverEvent = event;
        this.activePopover = type;
        
        // Use nextTick to ensure the event is properly captured
        this.$nextTick(() => {
          if (type === 'prefix') {
            this.$refs.prefixPopover.$el.present(event);
          } else if (type === 'suffix') {
            this.$refs.suffixPopover.$el.present(event);
          }
        });
      }
    },
    computed: {
      /**
       * Returns a list of example components for the preview
       * @returns {Array} - An array of component names with applied prefix/suffix
       */
      exampleComponents() {
        const components = [];
        const maxPreviewItems = 3;
        
        for (let i = 0; i < Math.min(this.amount, maxPreviewItems); i++) {
          const name = this.selected_component.name || 'Component';
          const prefixValue = this.prefix ? (this.prefixCountUp ? this.incrementCounter(this.prefix, i) : this.prefix) : '';
          const suffixValue = this.suffix ? (this.suffixCountUp ? this.incrementCounter(this.suffix, i) : this.suffix) : '';
          
          // Create the formatted name with appropriate spacing
          let formattedName = name;
          if (prefixValue) {
            formattedName = prefixValue + ' ' + formattedName;
          }
          if (suffixValue) {
            formattedName = formattedName + ' ' + suffixValue;
          }
          
          components.push(formattedName);
        }
        
        return components;
      },
      
      /**
       * Returns the complete list of components with their properties
       * @returns {Array} - An array of component objects
       */
      allComponents() {
        const components = [];
        
        for (let i = 0; i < this.amount; i++) {
          const name = this.selected_component.name || 'Component';
          const prefixValue = this.prefix ? (this.prefixCountUp ? this.incrementCounter(this.prefix, i) : this.prefix) : '';
          const suffixValue = this.suffix ? (this.suffixCountUp ? this.incrementCounter(this.suffix, i) : this.suffix) : '';
          
          // Create the formatted name with appropriate spacing
          let fullName = name.trim();
          if (prefixValue) {
            fullName = prefixValue.trim() + ' ' + fullName;
          }
          if (suffixValue) {
            fullName = fullName + ' ' + suffixValue.trim();
          }
          
          // Create a new component object with all properties from the selected component
          components.push({
            ...this.selected_component,
            name: fullName,
            order_id: i
          });
        }
        
        return components;
      }
    },
    watch: {
      allComponents: {
        handler() {
          this.$emit('update', this.allComponents);
        },
        deep: true
      },
      component: {
        handler() {
          this.selected_component = { ...this.component };
        },
        deep: true
      },
      amount: {
        handler(newValue) {
          // Automatically enable suffix numbering when creating multiple components
          if (newValue > 1 && !this.suffixCountUp && !this.prefixCountUp) {
            this.suffixCountUp = true;
            
            // If there's no suffix yet, add a default one
            if (!this.suffix) {
              this.suffix = '1';
            }
          }
        }
      }
    }
  }
  </script>
  
  <style>
  .prefix-suffix-container {
    margin-bottom: 20px;
  }
  
  .component-name-wrapper {
    background: var(--ion-color-custom-white);
    border: 1px solid var(--ion-color-medium-tint);
    border-radius: 8px;
    padding: 10px;
    display: flex;
    justify-content: center;
  }
  
  .controls-wrapper {
    display: flex;
    gap: 10px;
    margin-bottom: 12px;
  }
  
  .control-button {
    flex: 1;
    background: var(--ion-color-light);
    border: 1px solid var(--ion-color-medium-tint);
    border-radius: 8px;
    padding: 10px 12px;
    cursor: pointer;
    transition: background-color 0.2s, transform 0.1s;
    display: flex;
    flex-direction: column;
  }
  
  .control-button:hover {
    background-color: var(--ion-color-light-shade);
    transform: translateY(-1px);
  }
  
  .control-button:active {
    transform: translateY(1px);
  }
  
  .button-label {
    font-size: 12px;
    color: var(--ion-color-medium);
    margin-bottom: 5px;
    font-weight: 500;
  }
  
  .button-value {
    color: var(--ion-color-primary);
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  .button-placeholder {
    color: var(--ion-color-medium);
    font-style: italic;
    font-size: 13px;
  }
  
  .component-input {
    width: 100%;
    border: none;
    text-align: center;
    background-color: transparent;
    color: var(--ion-color-dark);
    font-size: 16px;
    font-weight: 500;
    padding: 8px;
    min-height: 40px;
  }
  
  .component-input:focus {
    outline: none;
    border-bottom: 2px solid var(--ion-color-primary);
  }
  
  .component-input::placeholder {
    color: var(--ion-color-medium);
    opacity: 0.7;
  }
  
  .amount-selector {
    margin-top: 8px;
  }
  
  .preview-list {
    margin-top: 16px;
    border-radius: 8px;
    overflow: hidden;
  }
  
  .preview-header {
    background-color: var(--ion-color-light);
    color: var(--ion-color-medium);
    font-size: 14px;
    padding: 8px 16px;
  }
  
  .settings-popover {
    --width: 280px;
    --border-radius: 8px;
  }
  
  .popover-list {
    padding: 8px;
  }
  
  .help-text {
    font-size: 12px;
    color: var(--ion-color-medium);
    padding: 0 16px;
  }
  
  .more-items-indicator {
    background-color: var(--ion-color-light);
    font-size: 14px;
  }
  </style>